import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// eslint-disable-next-line no-restricted-imports -- pre-existing thunk-level apiClient usage; migrate to api/<domain>.js when touched
import apiClient from '@sophocles/http-common';

// Async thunks
export const fetchOperationsDashboard = createAsyncThunk(
  'dashboards/fetchOperations',
  async ({ customerId, hours = 24, siteGroupIds, textSearch }, { rejectWithValue }) => {
    try {
      const params = { hours };
      if (siteGroupIds?.length > 0) params.site_group = siteGroupIds.join(',');
      if (textSearch) params.text_search = textSearch;
      const response = await apiClient.get(`/dashboard/operations/${customerId}/`, { params });
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const fetchManagementDashboard = createAsyncThunk(
  'dashboards/fetchManagement',
  async ({ customerId, period = '30d' }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/dashboard/management/${customerId}/`, { params: { period } });
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

const initialState = {
  operations: {
    data: null,
    loading: false,
    error: null,
  },
  management: {
    data: null,
    loading: false,
    error: null,
  },
};

const dashboardsSlice = createSlice({
  name: 'dashboards',
  initialState,
  reducers: {
    clearOperationsData: state => {
      state.operations.data = null;
      state.operations.error = null;
    },
    clearManagementData: state => {
      state.management.data = null;
      state.management.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Operations
      .addCase(fetchOperationsDashboard.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(fetchOperationsDashboard.fulfilled, (state, action) => {
        state.operations.loading = false;
        state.operations.data = action.payload;
      })
      .addCase(fetchOperationsDashboard.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error = action.payload || action.error.message;
      })
      // Management
      .addCase(fetchManagementDashboard.pending, state => {
        state.management.loading = true;
        state.management.error = null;
      })
      .addCase(fetchManagementDashboard.fulfilled, (state, action) => {
        state.management.loading = false;
        state.management.data = action.payload;
      })
      .addCase(fetchManagementDashboard.rejected, (state, action) => {
        state.management.loading = false;
        state.management.error = action.payload || action.error.message;
      });
  },
});

export const { clearOperationsData, clearManagementData } = dashboardsSlice.actions;

// Selectors
export const selectOperationsData = state => state.dashboards.operations.data;
export const selectOperationsLoading = state => state.dashboards.operations.loading;
export const selectOperationsError = state => state.dashboards.operations.error;
export const selectManagementData = state => state.dashboards.management.data;
export const selectManagementLoading = state => state.dashboards.management.loading;
export const selectManagementError = state => state.dashboards.management.error;

export default dashboardsSlice.reducer;
